import {
  EProvider,
  type TGlobalSearchGateResolver,
} from "@frontend-shared/types";
import { useCallback, useMemo } from "react";

import { useActiveOrganizationUuid } from "@/hooks/use-active-organization-uuid";
import { useAuth } from "@/hooks/use-auth";
import { buildGlobalSearchGateState } from "@/lib/global-search/build-global-search-gate-state";

import type { IUseGlobalSearchGateResolverProps } from "../types";
import { useGlobalSearchFlagInputs } from "./use-global-search-flag-inputs";
import { useGlobalSearchOrganizationInputs } from "./use-global-search-organization-inputs";
import { useGlobalSearchPermissionInputs } from "./use-global-search-permission-inputs";

// NOTE: NSAI-6589 — web's TGlobalSearchGateResolver. A gate that is not in the
// computed state (a gate added by a newer shared package) answers closed.
export const useGlobalSearchGateResolver = ({
  isEnabled,
}: IUseGlobalSearchGateResolverProps): TGlobalSearchGateResolver => {
  const { user } = useAuth();
  const activeOrganizationUuid = useActiveOrganizationUuid();
  const permissions = useGlobalSearchPermissionInputs();
  const organization = useGlobalSearchOrganizationInputs({ isEnabled });
  const flags = useGlobalSearchFlagInputs({
    ownedOrganizationUuid:
      isEnabled && organization.isOrganizationOwner
        ? (activeOrganizationUuid ?? undefined)
        : undefined,
  });
  const hasPasswordSignIn = user?.provider.includes(EProvider.EMAIL) ?? false;

  const openGates = useMemo(() => {
    const gateState = buildGlobalSearchGateState({
      permissions,
      flags,
      organization,
      hasPasswordSignIn,
    });
    return new Set<string>(
      Object.entries(gateState)
        .filter(([, isOpen]) => isOpen)
        .map(([gate]) => gate),
    );
  }, [permissions, flags, organization, hasPasswordSignIn]);

  return useCallback(
    (gate: string): boolean => openGates.has(gate),
    [openGates],
  );
};
